import { useEffect, useRef } from "react";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

import { triggerBrowserDownload } from "../download";
import { useZipTask } from "../hooks";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api/v1";

export interface ZipJob {
  taskId: string;
  folderName: string;
}

interface Props {
  job: ZipJob;
  onDismiss: () => void;
}

/** Floating card that tracks a folder-zip background task and downloads it when ready. */
export function ZipDownloadToast({ job, onDismiss }: Props) {
  const { data: task } = useZipTask(job.taskId);
  const downloaded = useRef(false);

  useEffect(() => {
    if (task?.status === "success" && !downloaded.current) {
      downloaded.current = true;
      const result = task.result as { token?: string; download_name?: string } | null;
      if (result?.token) {
        // The backend streams the archive and deletes it right after the download.
        const url = `${API_BASE}/two-drive/zip-download/?t=${encodeURIComponent(result.token)}`;
        triggerBrowserDownload(url, result.download_name ?? `${job.folderName}.zip`);
      }
      window.setTimeout(onDismiss, 4000);
    }
    if (task?.status === "failure") {
      window.setTimeout(onDismiss, 6000);
    }
  }, [task?.status, task?.result, job.folderName, onDismiss]);

  const status = task?.status ?? "pending";

  return (
    <div className="flex items-center gap-3 border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-4 py-3 shadow-lg min-w-[260px]">
      {status === "success" ? (
        <CheckCircle2 size={18} className="text-green-600 flex-shrink-0" />
      ) : status === "failure" ? (
        <XCircle size={18} className="text-[var(--color-primary)] flex-shrink-0" />
      ) : (
        <Loader2 size={18} className="animate-spin text-blue-600 flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm truncate">{job.folderName}.zip</p>
        <p className="text-xs font-mono text-[var(--color-text-muted)]">
          {status === "success"
            ? "Gotowe — pobieranie…"
            : status === "failure"
              ? "Nie udało się spakować"
              : "Pakowanie w toku…"}
        </p>
      </div>
      <button
        onClick={onDismiss}
        className="text-xs font-mono text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
      >
        ✕
      </button>
    </div>
  );
}
